import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getContentLayouts } from '../features/edit-page/api/layoutApi';

// Compatibility comes from the server for every destination, including DPI.
export default function BulkLayoutTemplateApply({ pages, language, disabled, onBusyChange, onApplied }) {
  const pageIdsKey = JSON.stringify(pages.map((page) => page._id).sort());
  const pageIds = useMemo(() => JSON.parse(pageIdsKey), [pageIdsKey]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [mode, setMode] = useState('empty');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState('');
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setTemplates([]); setTemplateId(''); setConfirming(false); setMessage(''); setLoadError('');
    if (!pageIds.length) { setLoading(false); return undefined; }
    setLoading(true);
    Promise.all(pageIds.map((id) => getContentLayouts(undefined, id))).then((responses) => {
      if (cancelled) return;
      const lists = responses.map((response) => response.data.content_layouts || []);
      setTemplates(lists[0].filter((template) => lists.every((list) => list.some((item) => item._id === template._id))));
    }).catch(() => {
      if (!cancelled) setLoadError('Could not load compatible templates.');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pageIds, reload]);

  const apply = async () => {
    if (!templateId || !pageIds.length || busy || disabled) return;
    setBusy(true); onBusyChange(true); setConfirming(false);
    let applied = 0; let skipped = 0;
    try {
      // Stop on failure: successful earlier pages stay saved and are reported.
      for (const page of pages) {
        setMessage(`Applying layout to page ${page.page_number}…`);
        try {
          const response = await axios.post(`/api/content-layouts/${templateId}/copy-to/${page._id}`,
            { language, only_empty: mode === 'empty' }, { withCredentials: true });
          if (response.data.skipped) skipped += 1;
          else { applied += 1; onApplied(response.data.page); }
        } catch (error) {
          setMessage(`${applied} applied, ${skipped} skipped. Stopped at page ${page.page_number}: ${error.response?.data?.error || 'Could not confirm the save. Check this page before retrying.'}`);
          return;
        }
      }
      setMessage(`${applied} layouts applied, ${skipped} skipped. Generate page renders when ready.`);
    } finally { setBusy(false); onBusyChange(false); }
  };

  return (
    <section className="space-y-3 rounded-lg border border-violet-200 bg-violet-50/40 p-3">
      <h3 className="text-sm font-semibold">Apply layout template</h3>
      <p className="text-xs text-gray-600">Each page gets its own copy for all languages. Wording and translations are kept.</p>
      <label className="block text-sm">
        Compatible template
        <select className="mt-1 w-full rounded border p-2 text-sm" value={templateId}
          disabled={disabled || busy || loading} onChange={(event) => { setTemplateId(event.target.value); setConfirming(false); }}>
          <option value="">{loading ? 'Loading templates…' : 'Choose a template'}</option>
          {templates.map((template) => <option key={template._id} value={template._id}>{template.name}</option>)}
        </select>
      </label>
      {loadError && <p role="alert" className="text-xs text-red-700">{loadError} <button type="button" className="underline" onClick={() => setReload((value) => value + 1)}>Retry</button></p>}
      {!loading && !loadError && !templates.length && <p className="text-xs text-gray-600">{pageIds.length ? 'No templates match every target page’s dimensions. Select pages with the same format.' : 'Select pages first.'}</p>}
      <label className="block text-sm">
        Apply mode
        <select className="mt-1 w-full rounded border p-2 text-sm" value={mode} disabled={disabled || busy}
          onChange={(event) => { setMode(event.target.value); setConfirming(false); }}>
          <option value="empty">Only pages with an empty layout</option>
          <option value="replace">Replace existing layouts</option>
        </select>
      </label>
      <p className="text-xs text-gray-600">{mode === 'empty' ? 'Pages with any saved boxes, including hidden boxes, are skipped. Pages without wording can receive the layout.' : 'Replaces the arrangement on every target page. Saved renders stay unchanged until you render again.'}</p>
      {confirming ? <div className="space-y-2 rounded border border-amber-300 bg-amber-50 p-2">
        <p className="text-xs">Replace the private layout on {pages.length} pages for all languages?</p>
        <button type="button" className="w-full rounded bg-violet-700 p-2 text-sm text-white" disabled={disabled || busy} onClick={apply}>Confirm replacement</button>
        <button type="button" className="w-full text-sm underline" onClick={() => setConfirming(false)}>Cancel</button>
      </div> : <button type="button" className="w-full rounded bg-violet-700 p-2 text-sm text-white disabled:opacity-50"
        disabled={disabled || busy || loading || !templateId || !pageIds.length}
        onClick={() => mode === 'replace' ? setConfirming(true) : apply()}>{busy ? 'Applying layout…' : `Apply layout to ${pages.length} pages`}</button>}
      {message && <p role="status" className="text-xs text-gray-700">{message}</p>}
    </section>
  );
}
